import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import cn from 'classnames'
import styles from './VNMenuItem.module.scss'
import { ButtonBase } from '@material-ui/core'
import useCartItem from '../hooks/useCartItem'
import VNMenuQuantity from './VNMenuQuantity'
import { OPEN_PRICE_ITEM_DISPLAY } from '../consts'
import { isColorTooLight } from '../../VNUtil/VNUtils'
import useLongPress from '../../VNUtil/hooks/useLongPress'
import FavoriteIcon from '../../VNCategories/components/VNFavoriteIcon'
import { toggleFavoriteItem } from '../../VNCategories/ActionCreators'
import { getCartItemIndex, getItemModifiedQuantity, getItemQuantity } from '../../selectors/cart'
import { getDisplayPrice } from '../../utils/items'
import { useCartProvider } from '../hooks/VNCartContext'
import { getBarcodeScanningSettings } from '../../selectors/config'
import ScanItemsIcon from '../../assets/icons/ScanItemsIconButton'
import useGetIsMobileScreen from '../../hooks/useGetIsMobileScreen'
import { isE700Device } from '../../VNAndroidSDK/Utils'

const ANIMATION_THREASHOLD = 1.5 // seconds
const LONG_PRESS_THRESHOLD = 675 // milliseconds
const LONG_PRESS_REMOVE_THRESHOLD = 900 // milliseconds
const DOUBLE_CLICK_GUARD_MS = 50

/**
 * MenuItem component
 */
const VNMenuItem = ({
  data: {
    color,
    hasRequiredModifiers = false,
    id: itemId,
    isAlcohol = false,
    orderMaxAmount: max,
    orderMinCount: min,
    name,
    price,
    itemCategoryUuid: categoryId,
    isFavorite = false,
    favoriteUpdatedAt = null,
    inventory,
    variants,
    barcode,
  },
  throttlingRef,
  throttlingActiveRef,
  isAddingToCartRef,
  isFavoritesEnabled,
}) => {
  const dispatch = useDispatch()
  const cartQuantity = useSelector((state) => getItemQuantity(state, itemId))
  const cartQuantityWithModifiers = useSelector((state) => getItemModifiedQuantity(state, itemId))
  const cartIndex = useSelector((state) => getCartItemIndex(state, itemId))
  const isInCartRef = useRef(cartIndex !== -1)
  const sumQuantity = cartQuantityWithModifiers + cartQuantity
  const quantitiesRef = useRef(sumQuantity)
  const [localQuantity, setLocalQuantity] = useState(quantitiesRef.current)
  const opacity = useMemo(() => (sumQuantity >= max ? 0.6 : 1), [sumQuantity, max])
  const displayPrice = getDisplayPrice({ orderMaxAmount: max, price, inventory, variants }, sumQuantity)
  const unavailable = useMemo(() => displayPrice === 'SOLD OUT', [displayPrice])
  const localStyle = useMemo(() => {
    return color || sumQuantity >= max ? { backgroundColor: color, opacity } : null
  }, [color, max, sumQuantity, opacity])
  const isTooLight = useMemo(() => isColorTooLight(localStyle?.backgroundColor), [localStyle])
  const { isCartLocked } = useCartProvider()
  const barcodeScanSettings = useSelector(getBarcodeScanningSettings)
  const isMobile = useGetIsMobileScreen()
  const lastClickTimeRef = useRef(0)

  const { addNonRegularItem, upsertItem } = useCartItem({
    throttlingRef,
    throttlingActiveRef,
    isInCartRef,
    quantitiesRef,
  })

  const isScannableItem = useMemo(() => {
    return barcodeScanSettings?.displayScannableItems && barcode && !unavailable && sumQuantity < max && !isMobile
  }, [barcodeScanSettings, barcode, sumQuantity, max, unavailable, isMobile])

  // resets to cart quantity when cart quantity changes outside of component
  useEffect(() => {
    if (throttlingActiveRef.current[itemId]) return
    const curQuantity = hasRequiredModifiers ? cartQuantityWithModifiers : cartQuantity
    if (cartQuantity === 0 && cartQuantityWithModifiers === 0) {
      quantitiesRef.current = 0
      setLocalQuantity(0)
      isInCartRef.current = cartIndex !== -1
      return
    }
    if (sumQuantity !== localQuantity) {
      setLocalQuantity(sumQuantity)
    }
    if (curQuantity === quantitiesRef.current) return

    quantitiesRef.current = curQuantity
    isInCartRef.current = cartIndex !== -1
  }, [
    cartIndex,
    cartQuantity,
    cartQuantityWithModifiers,
    sumQuantity,
    hasRequiredModifiers,
    itemId,
    localQuantity,
    quantitiesRef,
    throttlingActiveRef,
  ])

  /**
   * Update local quantity when tapping on item
   */
  const handleUpdateQuantities = useCallback(() => {
    if (!quantitiesRef.current) {
      setLocalQuantity(min || 1)
      quantitiesRef.current = min || 1
      return
    }

    quantitiesRef.current++
    setLocalQuantity(cartQuantityWithModifiers + cartQuantity + 1)
  }, [cartQuantity, cartQuantityWithModifiers, min])

  const updateLocalQuantities = useCallback((quantity) => {
    setLocalQuantity(quantity)
    quantitiesRef.current = quantity
  }, [])

  /**
   * Add item to cart with throttling functionality
   */
  const handleAddToCart = useCallback(() => {
    if (isCartLocked()) return
    if (sumQuantity >= max) return

    const now = Date.now()
    if (now - lastClickTimeRef.current < DOUBLE_CLICK_GUARD_MS) return
    lastClickTimeRef.current = now

    // This is the quantity before updating the cart
    const localCartQuantity = cartQuantityWithModifiers + cartQuantity

    if (hasRequiredModifiers || displayPrice === OPEN_PRICE_ITEM_DISPLAY) {
      addNonRegularItem({ itemId, isAlcohol })
      return
    }

    // Update local quantity immediately
    handleUpdateQuantities()

    upsertItem({
      itemId,
      isAlcohol,
      hasRequiredModifiers,
      max,
      cartIndex,
      quantity: quantitiesRef.current ?? 1,
      // This function will be called to revert local quantity if cart is locked when processing the add/upsert
      updateLocalQuantities: () => updateLocalQuantities(localCartQuantity),
    })
  }, [
    max,
    handleUpdateQuantities,
    hasRequiredModifiers,
    upsertItem,
    itemId,
    isAlcohol,
    cartIndex,
    quantitiesRef,
    addNonRegularItem,
    sumQuantity,
    displayPrice,
    cartQuantity,
    cartQuantityWithModifiers,
    isCartLocked,
    updateLocalQuantities,
  ])

  const handleLongPress = useCallback(
    (e) => {
      e.preventDefault()
      dispatch(toggleFavoriteItem(itemId, categoryId))
    },
    [itemId, categoryId, dispatch],
  )

  const longPressProps = useLongPress(
    handleLongPress,
    handleAddToCart,
    isAddingToCartRef,
    itemId,
    isFavorite ? LONG_PRESS_REMOVE_THRESHOLD : LONG_PRESS_THRESHOLD,
  )
  const animationThreshold = moment().subtract(ANIMATION_THREASHOLD, 'seconds')

  return (
    <ButtonBase
      disableRipple={isE700Device()}
      component="div"
      className={cn(styles.container, {
        [styles.unavailable]: unavailable,
        [styles.isFavorite]: isFavorite,
        [styles.animated]: favoriteUpdatedAt && moment(favoriteUpdatedAt).isAfter(animationThreshold),
        [styles.darker]: isTooLight,
      })}
      style={localStyle}
      {...(isFavoritesEnabled ? longPressProps : { onClick: handleAddToCart })}
    >
      {isFavorite && <FavoriteIcon className={styles.favorite} />}
      <div className={styles.title}>
        <span className={cn(styles.name)}>{name}</span>
      </div>
      <div className={styles.details}>
        <div className={styles.inventoryPrice}>
          <span className={cn(styles.price)}>{displayPrice}</span>
        </div>
        {isScannableItem && <ScanItemsIcon color={isTooLight ? 'black' : 'white'} style={{ marginLeft: 'auto' }} />}
        <VNMenuQuantity isScannableItem={isScannableItem} color={color} value={localQuantity} useDark={isTooLight} />
      </div>
    </ButtonBase>
  )
}

export default VNMenuItem
